import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { ArrowLeft, EyeOff, History, Loader2, Trophy } from 'lucide-react'
import { useTeamStore } from '../store/useTeamStore'
import useAppStore from '../../../store/useAppStore'
import TeamStatsTotals from '../components/TeamStatsTotals'
import TeamModesBreakdown from '../components/TeamModesBreakdown'
import TeamMemberStatsList from '../components/TeamMemberStatsList'
import { accentColor, lightenHex } from '../utils/teamColors'
import { emptyDayText, PERIODS, periodLabel, statsErrorText } from '../utils/teamStats'
import { CARD_CLASS, SECTION_VARIANT, SECTIONS_VARIANT } from '../utils/teamUi'
import { hapticImpact } from '../../../utils/haptic'
import useAudioManager from '../../../hooks/useAudioManager'

/**
 * Статистика командных игр банды (`/teams/:slug/stats`, docs/team-stats-api.md §3).
 *
 * Игра считается командной, если в ней сыграли 2+ участника одной банды: в
 * раундовых режимах — в одном раунде, в кейсах/апгрейдере/контракте — сессией
 * в одном режиме с паузой не больше 10 минут. Одиночные игры сюда не попадают.
 *
 * Статистику видно по `can_view_stats` из карточки банды: участнику всегда,
 * чужому — по тумблеру `show_stats`. При `false` ручка отвечает 403
 * `stats_hidden` — кнопки на карточке нет, но по прямой ссылке экран должен
 * объяснить, почему пусто.
 */
const TeamStats = () => {
  const { slug } = useParams()
  const isGuest = useAppStore((state) => state.isGuest)
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { playSound } = useAudioManager({ pick: 'pick', select: 'select' })
  const { team, loadTeam, stats, isStatsLoading, statsError, loadStats, resetStats } = useTeamStore()

  const [period, setPeriod] = useState('all')

  // `/teams/my/stats` — свою банду сначала надо опознать: ручка статистики
  // работает только по настоящему slug
  const statsSlug = slug === 'my' ? team?.id : slug

  useEffect(() => {
    loadTeam(slug)
    return () => resetStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, isGuest])

  useEffect(() => {
    if (statsSlug) loadStats(statsSlug, period)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statsSlug, period, isGuest])

  const accent = accentColor(team?.colors)
  const level = Number.isFinite(stats?.level) ? stats.level : null
  // Прогресс приходит только участникам, чужому — null
  const hasProgress = typeof stats?.level_progress_pct === 'number'
  const progressPct = hasProgress ? Math.min(100, Math.max(0, stats.level_progress_pct)) : 0
  const isEmptyDay = ['today', 'yesterday'].includes(stats?.period) && Number(stats?.totals?.games) === 0

  const goBack = () => {
    playSound('select')
    hapticImpact('light')
    if (window.history.length > 1) navigate(-1)
    else navigate(`/teams/${slug}`)
  }

  return (
    <div className="mx-auto w-full px-4 py-6 lg:max-w-3xl lg:px-6 lg:py-8">
      <motion.div variants={SECTIONS_VARIANT} initial="hidden" animate="visible" className="space-y-5">
        <motion.div variants={SECTION_VARIANT} className="flex items-center gap-3">
          <button
            onClick={goBack}
            className="rounded-lg bg-surface p-2 text-white transition-all active:scale-[0.98]"
            aria-label={t('common.back', 'Назад')}
          >
            <ArrowLeft size={20} />
          </button>
          <div className="min-w-0">
            <h1 className="truncate text-2xl text-white">{t('team.stats.title', 'Статистика банды')}</h1>
            {team?.name && <p className="truncate text-xs text-text-secondary">{team.name}</p>}
          </div>
        </motion.div>

        {/* Период: сутки по МСК, 7/30 дней и всё время (по умолчанию) */}
        <motion.div variants={SECTION_VARIANT} className="flex flex-wrap gap-2">
          {PERIODS.map((value) => {
            const isActive = value === period
            return (
              <button
                key={value}
                onClick={() => {
                  if (isActive) return
                  playSound('pick')
                  hapticImpact('light')
                  setPeriod(value)
                }}
                className="min-w-[calc(33.333%_-_0.5rem)] flex-1 rounded-xl border px-3 py-2 text-xs font-semibold transition-all active:scale-[0.98]"
                style={isActive
                  ? { borderColor: `${accent}55`, backgroundColor: `${accent}14`, color: accent }
                  : { borderColor: 'rgba(255,255,255,0.10)', backgroundColor: 'rgba(255,255,255,0.03)', color: '#A0A0A0' }}
              >
                {periodLabel(value, t)}
              </button>
            )
          })}
        </motion.div>

        {statsError ? (
          <motion.div variants={SECTION_VARIANT} className={`${CARD_CLASS} flex flex-col items-center p-8 text-center`}>
            <span className="mb-3 flex h-14 w-14 items-center justify-center rounded-full border border-white/10 bg-dark-primary">
              <EyeOff size={24} className="text-text-secondary" />
            </span>
            <p className="text-sm text-text-secondary">{statsErrorText(statsError, t)}</p>
          </motion.div>
        ) : isStatsLoading && !stats ? (
          <div className="flex justify-center py-16">
            <Loader2 size={24} className="animate-spin text-accent-primary" />
          </div>
        ) : stats ? (
          <>
            {/* Уровень банды: растёт по кубической кривой от оборота всего состава,
                поэтому прогресс-бар только из level_progress_pct — своих формул нет */}
            {level !== null && (
              <motion.div variants={SECTION_VARIANT} className={`${CARD_CLASS} relative overflow-hidden p-5`}>
                <div
                  className="pointer-events-none absolute -right-10 -top-16 h-40 w-40 rounded-full blur-2xl"
                  style={{ backgroundColor: `${accent}1A` }}
                />
                <div className="relative">
                  <div className="mb-1 flex items-center gap-2">
                    <Trophy size={16} style={{ color: accent }} />
                    <span className="text-xs uppercase tracking-wide text-text-secondary">
                      {t('team.level', 'Уровень банды')}
                    </span>
                  </div>
                  <div
                    className="bg-clip-text text-3xl font-bold tabular-nums text-transparent"
                    style={{ backgroundImage: `linear-gradient(90deg, ${accent}, ${lightenHex(accent)})` }}
                  >
                    {level}
                  </div>

                  {hasProgress ? (
                    <div className="mt-3">
                      <div className="mb-1 flex items-center justify-between text-[11px] text-text-secondary">
                        <span>{t('team.levelNext', 'До {{level}} уровня', { level: level + 1 })}</span>
                        <span className="font-semibold tabular-nums">{progressPct}%</span>
                      </div>
                      <div className="h-1.5 overflow-hidden rounded-full border border-white/5 bg-black/40">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${progressPct}%`, backgroundColor: accent, boxShadow: `0 0 8px ${accent}66` }}
                        />
                      </div>
                    </div>
                  ) : (
                    <p className="mt-2 text-xs text-text-secondary">
                      {t('team.levelHint', 'Уровень поднимают только командные игры — те, где сыграли двое и больше из банды')}
                    </p>
                  )}
                </div>
              </motion.div>
            )}

            {isEmptyDay ? (
              <motion.div variants={SECTION_VARIANT} className={`${CARD_CLASS} flex flex-col items-center p-8 text-center`}>
                <span className="mb-3 flex h-14 w-14 items-center justify-center rounded-full border border-white/10 bg-dark-primary">
                  <History size={24} className="text-text-secondary" />
                </span>
                <p className="text-sm text-text-secondary">{emptyDayText(stats.period, t)}</p>
              </motion.div>
            ) : (
              <>
                <motion.div variants={SECTION_VARIANT}>
                  <TeamStatsTotals totals={stats.totals} accent={accent} />
                </motion.div>

                <motion.div variants={SECTION_VARIANT}>
                  <TeamModesBreakdown modes={stats.modes} accent={accent} />
                </motion.div>

                <motion.div variants={SECTION_VARIANT}>
                  <TeamMemberStatsList members={stats.members} accent={accent} />
                </motion.div>
              </>
            )}

            <motion.button
              variants={SECTION_VARIANT}
              onClick={() => {
                playSound('select')
                navigate(`/teams/${statsSlug}/games`)
              }}
              className="flex w-full items-center justify-center gap-2 rounded-xl border p-3.5 text-sm font-bold transition-all active:scale-[0.98]"
              style={{ borderColor: `${accent}55`, color: accent, backgroundColor: `${accent}14` }}
            >
              <History size={16} />
              {t('team.stats.toGames', 'История командных игр')}
            </motion.button>
          </>
        ) : null}
      </motion.div>
    </div>
  )
}

export default TeamStats
